import {
  api,
  ApiError,
  getContinuousMonitoringPolicy,
  listContinuousIncidents,
  listContinuousMonitors,
  listProjectDeployments,
  listRecoveryPoints,
} from './api';

/**
 * What the dashboard reads, by name: one definition of each read's cache key
 * and loader, used by the pages that show it and by the prefetcher that warms
 * it, so a hover can only ever warm exactly what the page will ask for.
 *
 * Keys name their project or environment, so a live event refreshes just what
 * it touched (see liveRefresh.ts) and nothing from one project can be shown
 * for another.
 */
export interface Query<T> {
  key: string;
  load: () => Promise<T>;
}

export const queries = {
  overview: (): Query<Awaited<ReturnType<typeof api.overview>>> => ({ key: 'overview', load: () => api.overview() }),
  organizationHealth: () => ({ key: 'org-health', load: () => api.getOrganizationHealth() }),
  projects: () => ({ key: 'projects', load: () => api.listProjects() }),
  serverRows: () => ({ key: 'servers:rows', load: () => api.listServers().then((res) => res.servers) }),
  auditEvents: () => ({ key: 'audit-events', load: () => api.listAuditEvents() }),
  /** The organization's people, so a deployment's initiator shows a name the
   * team chose rather than one guessed from an email (CO-150). A failure is
   * not an error: initiators then show as recorded. */
  members: () => ({ key: 'members', load: () => api.listMembers().catch(() => ({ members: [] })) }),

  project: (projectId: string) => ({ key: `project:${projectId}`, load: () => api.getProject(projectId) }),
  servers: () => ({ key: 'servers', load: () => api.listServers() }),
  applications: (projectId: string) => ({
    key: `project:${projectId}:applications`,
    load: () => api.listProjectApplications(projectId).catch(() => ({ applications: [] })),
  }),
  historyApplications: (projectId: string) => ({
    key: `project:${projectId}:applications-history`,
    load: () => api.listProjectApplications(projectId, true).catch(() => ({ applications: [] })),
  }),
  incidents: (projectId: string) => ({
    key: `project:${projectId}:incidents`,
    load: () => listContinuousIncidents(projectId, 'active').catch(() => ({ incidents: [] })),
  }),

  /** The monitoring section: its monitors, open incidents and thresholds, read together. */
  monitoring: (projectId: string) => ({
    key: `project:${projectId}:monitoring`,
    load: async () => {
      const [monitors, incidents, policy] = await Promise.all([
        listContinuousMonitors(projectId),
        listContinuousIncidents(projectId, 'active'),
        getContinuousMonitoringPolicy(projectId),
      ]);
      return { monitors: monitors.monitors || [], incidents: incidents.incidents || [], policy };
    },
  }),

  environmentOverview: (projectId: string, environmentId: string | null) => ({
    key: `project:${projectId}:env:${environmentId}:overview`,
    load: () => (environmentId ? api.getEnvironmentOverview(environmentId) : Promise.resolve(null)),
  }),
  components: (projectId: string, environmentId: string | null) => ({
    key: `project:${projectId}:env:${environmentId}:components`,
    load: () => (environmentId ? api.getComponentsOverview(projectId, environmentId).catch(() => null) : Promise.resolve(null)),
  }),
  deployments: (projectId: string, environmentId: string | null) => ({
    key: `project:${projectId}:env:${environmentId}:deployments`,
    load: () =>
      environmentId
        ? listProjectDeployments(projectId, environmentId)
        : Promise.resolve({ deployments: [], serving: {} }),
  }),
  recoveryPoints: (projectId: string, environmentId: string | null) => ({
    key: `project:${projectId}:env:${environmentId}:recovery-points`,
    load: () =>
      environmentId
        ? listRecoveryPoints(projectId, environmentId).catch(() => ({ recovery_points: [] }))
        : Promise.resolve({ recovery_points: [] }),
  }),
  // The plan is a persisted record of the environment (CO-73): a 404 means
  // there is no plan yet; any other failure is shown, never read as "no
  // plan", which would silently hide an approved plan and its Run action.
  plan: (projectId: string, environmentId: string | null) => ({
    key: `project:${projectId}:env:${environmentId}:plan`,
    load: () =>
      environmentId
        ? api.getLatestOnboardingPlan(projectId, environmentId).catch((cause: unknown) => {
            if (cause instanceof ApiError && cause.status === 404) return null;
            throw cause;
          })
        : Promise.resolve(null),
  }),
  environmentHealth: (projectId: string, environmentId: string | null) => ({
    key: `project:${projectId}:env:${environmentId}:health`,
    load: () => (environmentId ? api.getEnvironmentHealth(environmentId) : Promise.resolve(null)),
  }),
};
